/**
 * Marketing Service
 * ---------------------------------------------------------------------------
 * Handles API calls related to Marketing domain data.
 *
 * Responsibilities:
 *  - Fetch account-level marketing info
 *  - Fetch account owner details
 *  - Fetch authenticated user info
 *
 * Notes:
 *  - Uses shared MarketingHttpClient
 *  - Supports skipping AccountId interceptor where required
 *
 * Author      : Harvinder Singh <harvinder@maropost.com>
 * Created On  : 2025-09-12
 * Last Update : 2026-02
 * ---------------------------------------------------------------------------
 */

import { ref } from 'vue'
import type { Ref } from 'vue'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { MarketingHttpClient } from '@/lib/repositories'

/* ============================================================================
 * TYPES
 * ============================================================================
 */

/**
 * Generic API response wrapper
 * (Used only where API actually wraps data under `data`)
 */
interface ApiResponse<T> {
  data: T
  meta?: Record<string, unknown>
}

/**
 * Account information payload
 */
export interface AccountInfo {
  [key: string]: unknown
}

/**
 * Account owner payload
 * NOTE: API returns `{ owner: { id } }`
 */
export interface OwnerInfo {
  owner: {
    id: number
  }
}

/**
 * Logged-in user payload
 * NOTE: API returns `{ user: {...} }`
 */
export interface UserInfo {
  user: Record<string, unknown>
}

/**
 * Axios config extension used by interceptors
 * (e.g. skipAccountId)
 */
type MarketingRequestConfig = AxiosRequestConfig & {
  skipAccountId?: boolean
}

/* ============================================================================
 * SERVICE
 * ============================================================================
 */

export function useMarketingService() {
  const httpClient = MarketingHttpClient

  /* --------------------------------------------------------------------------
   * STATE
   * --------------------------------------------------------------------------
   */
  const account: Ref<AccountInfo> = ref({})
  const owner: Ref<OwnerInfo | null> = ref(null)
  const user: Ref<UserInfo | null> = ref(null)

  /* --------------------------------------------------------------------------
   * API: Fetch Account Info
   * Response shape: { data: {...} }
   * --------------------------------------------------------------------------
   */
  async function fetchAccountInfo(
    config: MarketingRequestConfig = {}
  ): Promise<AxiosResponse<ApiResponse<AccountInfo>>> {
    const RESOURCE = `/show.json`

    try {
      const response = await httpClient.get<ApiResponse<AccountInfo>>(
        RESOURCE,
        config
      )

      account.value = response.data.data
      return response
    } catch (error) {
      throw error
    }
  }

  /* --------------------------------------------------------------------------
   * API: Fetch Account Owner Info
   * Response shape: { owner: {...} }
   * --------------------------------------------------------------------------
   */
  async function fetchAccountOwnerInfo(
    config: MarketingRequestConfig = {}
  ): Promise<AxiosResponse<OwnerInfo>> {
    const RESOURCE = `/owner.json`

    try {
      const response = await httpClient.get<OwnerInfo>(
        RESOURCE,
        config
      )

      owner.value = response.data
      return response
    } catch (error) {
      throw error
    }
  }

  /* --------------------------------------------------------------------------
   * API: Fetch Logged-in User Info
   * Response shape: { user: {...} }
   *
   * NOTE:
   * - Account ID header must NOT be attached for this endpoint
   * - Explicitly opts out using `skipAccountId`
   * --------------------------------------------------------------------------
   */
  async function fetchUserInfo(
    config: MarketingRequestConfig = {}
  ): Promise<AxiosResponse<UserInfo>> {
    const RESOURCE = `/users/info.json`

    try {
      const response = await httpClient.get<UserInfo>(
        RESOURCE,
        {
          ...config,
          skipAccountId: true, //  opt-out of AccountId interceptor
        } as any
      )

      user.value = response.data
      return response
    } catch (error) {
      throw error
    }
  }

  /* --------------------------------------------------------------------------
   * PUBLIC API
   * --------------------------------------------------------------------------
   */
  return {
    // API methods
    fetchAccountInfo,
    fetchAccountOwnerInfo,
    fetchUserInfo,

    // reactive state
    account,
    owner,
    user,
  }
}
